<template lang="pug">
  .page-main-wat.form-top-padding(:class='sessionGetDisplayClass(["body", "content"])')
    //- Audit history modal
    .modal.fade#modalAudit(v-if='canSeeAudit', :class='sessionGetDisplayClass(["body", "content"])', tabindex='-1', role='dialog', aria-labelledby='modalAuditLabel', aria-hidden='true')
      .modal-dialog.modal-dialog-scrollable.modal-wide(role="document")
        .modal-content
          .modal-header
            h5.modal-title#modalAuditLabel Change History
            button.close(type='button', data-dismiss='modal', aria-label='Close')
              span(aria-hidden='true') &times;

          .modal-body
            p The table below shows the change history for this particular WAT record.  Changes between versions are highlighted.
            p
              small NOTE: Only fields that has had a change will show up here; check the current record to see all current values.  Freeform text changes will come in an upcoming update.
            table.table.table-striped.smaller-text(v-if='facultyData.audit && facultyData.audit.length > 0')
              thead
                th(v-for='auditCol in auditColumns') {{ auditCol.label }}
              tbody
                tr(v-for='auditEntry in facultyData.fullAudit')
                  td(v-for='auditCol in auditColumns', :class='includes(auditEntry.changed, auditCol.value) ? "bg-info text-white" : ""') {{ auditEntry[auditCol.value] }}
            p(v-else) There is no change history available, this is the original record. 😎

          .modal-footer
              button.btn.btn-secondary(type='button', data-dismiss='modal') Close

    //- Saving confirmation modal
    .modal.fade#modalSaveConfirm(:class='sessionGetDisplayClass(["body", "content"])', tabindex='-1', role='dialog', aria-labelledby='modalTemplateLabel', aria-hidden='true')
      .modal-dialog.modal-lg(role="document")
        .modal-content
          .modal-header
            h5.modal-title#modalTemplateLabel Updating Record Status to: {{ currentRecordStatusLabel }}
            button.close(type='button', data-dismiss='modal', aria-label='Close')
              span(aria-hidden='true') &times;

          .modal-body
            .row.mb-3
              .col(v-if='targetIsForReview || targetIsFinal') {{ templateHelpText }}

            .row.mb-3
              .col(v-if='canFinalizeWat && !targetIsFinal') You will still be able to edit this record afterwards.
              .col(v-else) You will NOT be able to edit this record afterwards.

            .row
              .col-6.text-center
                button.btn.btn-outline-primary(type='button', data-dismiss='modal', @click='doSaveAsDraft') No, save as Draft instead
              .col-6.text-center
                button.btn.btn-success(type='button', data-dismiss='modal', @click='doSave(currentRecordStatus)') Yes, save as {{ currentRecordStatusLabel }}


          .modal-footer
              button.btn.btn-outline-danger(type='button', data-dismiss='modal') Cancel

    .container-fluid.text-center(v-if='isLoading')
      Loading(:showMessages='true')

    .container-fluid.text-danger.text-center(v-if='!isLoading && showErrorMessage') {{ showErrorMessage }}

    .container-fluid.extra-padding.px-0(v-show='!isLoading && !showErrorMessage', :class='sessionGetDisplayClass(["content", "bg"])')

      //- Success toasts
      .toast.success-toast(role='alert', aria-live='assertive', aria-atomic='true', data-delay='2500', :style='toastPosition')
        .toast-header.bg-primary.text-light
          strong.mr-auto Save successful
          button.ml-2.mb-1.close(type='button', data-dismiss='toast', aria-label='Close')
            span.text-light(aria-hidden='true') &times;
        .toast-body {{ facultyData.EMPLOYEE_NAME }} has been saved.

      //- Error toasts
      .toast.error-toast(role='alert', aria-live='assertive', aria-atomic='true', data-delay='2500', :style='toastPosition')
        .toast-header.bg-danger.text-light
          strong.mr-auto Failed to save
          button.ml-2.mb-1.close(type='button', data-dismiss='toast', aria-label='Close')
            span.text-light(aria-hidden='true') &times;
        .toast-body {{ saveErrorMessage || "Oops, something went wrong, please try again." }}

      .sticky-header.show-small#the-sticky-header(:class='sessionGetDisplayClass("sticky-header")')
        .row.align-items-center.mb-2.py-1
          .col-auto
            router-link(v-if='fromHome', :to='getRouterLink("home")', @click.native='scrollToTop')
              font-awesome-icon.mr-2(:icon='["fas", "chevron-left"]')
              | Home
            router-link.link(v-else, :to='getRouterLink("templates")', @click.native='scrollToTop')
              font-awesome-icon.mr-2(:icon='["fas", "chevron-left"]')
              | All Faculty
          .col-auto.p-0.d-none.small-header-name
            h5.m-0 {{ facultyData.EMPLOYEE_NAME }}
          .col-auto.pl-2.pr-0.col-auto.d-none.small-header-name(v-if='workloadStatusIcon')
            font-awesome-icon(:class='workloadStatusClass', :icon='workloadStatusIcon', :title='hasMetWorkloadStatus')
          .col-auto.pl-2.mb-1.d-none.small-header-name
            span.badge(:class='getBadgeClass') {{ badgeLabel }}

      form.mt-1.pb-5.needs-validation(@change='makeDirty()')
        .row.mx-3
          .col-8.px-0
            .py-4.mb-3.text-white(v-if='!canEditWat', :class='sessionGetDisplayClass("section") + " " + recordStatusClass')
              .row
                .col-12 {{ getStatusLabel }}
            .py-4.mb-3(:class='sessionGetDisplayClass("section")')
              .row
                .col-12
                  .row
                    .col
                      h4.mb-0 Section I - GENERAL INFORMATION
                    .col-auto
                      button.btn.p-0.mb-1.ml-2.collapse-icon.when-opened(type="button" data-toggle="collapse" data-target="#section-general-collapse")
                        font-awesome-icon.mx-1(:icon='["fas", "chevron-up"]')
                      button.btn.p-0.mb-1.ml-2.collapse-icon.when-closed(type="button" data-toggle="collapse" data-target="#section-general-collapse")
                        font-awesome-icon.mx-1(:icon='["fas", "chevron-down"]')
                  hr.section-hr
              .collapse.show#section-general-collapse
                .row
                  .col-6
                    CoachmarkTip(:options='getCoachmarkData("0")')
                      label(for='jobType') Job Type
                      select.custom-select#jobType(v-if='canEditWat', v-model='facultyData.JOB_TYPE', @change='onJobTypeChange(facultyData)')
                        option(v-for='jobType in jobTypes', :value='jobType.value') {{ jobType.label }}
                      .font-weight-bold(v-else) {{ facultyData.JOB_TYPE }}

                  .col-auto
                    CoachmarkTip(:options='getCoachmarkData("1")')
                      label(for='facultyDepartmentFTE') Departmental Full-Time Equivalent
                      input.form-control#facultyDepartmentFTE(v-if='canEditWat', type='number', v-model='facultyData.FTE', :class='getNegativeAndRequiredValidation(facultyData.FTE)', min='0', @change='roundTopLevelValue("FTE")')
                      .font-weight-bold(v-else) {{ facultyData.FTE }}
                      .invalid-feedback FTE must be greater than 0
                .row
                  .col-auto.mt-3
                    CoachmarkTip(:options='getCoachmarkData("2")')
                      label(for='facultyExpectedTE') Expected {{ equivalencyLabel }} Equivalent
                      button.btn.p-0.mb-1.help-icon(type='button', data-toggle='tooltip', data-placement='top', :title='equivalencyLabelTooltip')
                        font-awesome-icon.mx-1(:icon='["far", "question-circle"]')
                      input.form-control#facultyExpectedTE(v-if='canEditWat', type='number', v-model='facultyData.FULL_LOAD_CREDITS', :class='getNegativeAndRequiredValidation(facultyData.FULL_LOAD_CREDITS)', min='0', @change='roundTopLevelValue("FULL_LOAD_CREDITS")')

                      .font-weight-bold(v-else) {{ facultyData.FULL_LOAD_CREDITS }}
                      .invalid-feedback Expected TE must be greater than 0
                template(v-if='canEditWat || hasAnyFacultyStatus')
                  hr
                  CoachmarkTip(:options='getCoachmarkData("3")')
                    .row
                      .col-12
                        h5.font-weight-bold.mb-0 FACULTY STATUS
                      .col
                        small.form-text.text-muted (Check all that apply)
                    .row.mb-2
                      .col
                        small.form-text.text-muted *Selecting at least one will indicate that the faculty was not able to make their workload and will be marked as N/A in the final report
                    .row.mx-auto
                      .col-6.form-check.px-4(v-for='status in facultyStatuses')
                        input.form-check-input(type='checkbox', :id='status.ATTRIBUTE_CODE', v-model='status.ATTRIBUTE_VALUE', :disabled='!canEditWat')
                        label.form-check-label(:for='status.ATTRIBUTE_CODE') {{ status.ATTRIBUTE_DESC }}
                        button.btn.p-0.mb-1.help-icon(v-if='status.TOOLTIP', type='button', data-toggle='tooltip', data-placement='top', :title='status.TOOLTIP')
                          font-awesome-icon.mx-1(:icon='["far", "question-circle"]')

            //- Instructional Section
            .py-4.mb-3(:class='sessionGetDisplayClass("section")')
              .row
                .col-12
                  .row
                    .col
                      h4.mb-0 Section II - INSTRUCTIONAL
                    .col-auto
                      button.btn.p-0.mb-1.ml-2.collapse-icon.when-opened(type="button" data-toggle="collapse" data-target="#section-instructional-collapse")
                        font-awesome-icon.mx-1(:icon='["fas", "chevron-up"]')
                      button.btn.p-0.mb-1.ml-2.collapse-icon.when-closed(type="button" data-toggle="collapse" data-target="#section-instructional-collapse")
                        font-awesome-icon.mx-1(:icon='["fas", "chevron-down"]')
                  hr.section-hr
              .collapse#section-instructional-collapse
                template(v-if='hasAnyInstructionalData')
                  .row
                    .col-12
                      h5 {{ getAcademicYearStart }}-{{ getAcademicYearEnd }} Academic Year
                  .row.form-group
                    .col-6(v-if='shouldShowValue(getFacultyInstructionalForCode(CODE_EST_CLASSROOM_INSTRUCTION).ATTRIBUTE_VALUE)')
                      CoachmarkTip(:options='getCoachmarkData("6")')
                        label(for='ayEstClassroomCredits') Estimated Classroom Instruction
                        button.btn.p-0.mb-1.help-icon(v-if='getFacultyInstructionalForCode(CODE_EST_CLASSROOM_INSTRUCTION).TOOLTIP', type='button', data-toggle='tooltip', data-placement='top', :title='getFacultyInstructionalForCode(CODE_EST_CLASSROOM_INSTRUCTION).TOOLTIP')
                          font-awesome-icon.mx-1(:icon='["far", "question-circle"]')
                        .font-weight-bold {{ getFacultyInstructionalForCode(CODE_EST_CLASSROOM_INSTRUCTION).ATTRIBUTE_VALUE }}

                    .col-6(v-if='shouldShowValue(getFacultyInstructionalForCode(CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_VALUE)')
                      CoachmarkTip(:options='getCoachmarkData("7")')
                        label(for='ayClassroomCredits') Actual Classroom Instruction
                        button.btn.p-0.mb-1.help-icon(v-if='getFacultyInstructionalForCode(CODE_ACTUAL_CLASSROOM_INSTRUCTON).TOOLTIP', type='button', data-toggle='tooltip', data-placement='top', :title='getFacultyInstructionalForCode(CODE_ACTUAL_CLASSROOM_INSTRUCTON).TOOLTIP')
                          font-awesome-icon.mx-1(:icon='["far", "question-circle"]')
                        input.form-control(v-if='canEditWat', type='number', v-model='getFacultyInstructionalForCode(CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_VALUE', :class='getNegativeNumberValidation(getFacultyInstructionalForCode(CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_VALUE)', min='0', @change='roundValue("INSTR", CODE_ACTUAL_CLASSROOM_INSTRUCTON)')
                        .font-weight-bold(v-else) {{ getFacultyInstructionalForCode(CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_VALUE }}
                        .invalid-feedback Credits must be greater than 0
                  .row.form-group
                    .col-6(v-if='shouldShowValue(getFacultyInstructionalForCode(CODE_INDIVIDUAL_INSTRUCTION).ATTRIBUTE_VALUE)')
                      CoachmarkTip(:options='getCoachmarkData("8")')
                        label(for='ayIndividualCredits') Individual Instruction
                        button.btn.p-0.mb-1.help-icon(v-if='getFacultyInstructionalForCode(CODE_INDIVIDUAL_INSTRUCTION).TOOLTIP', type='button', data-toggle='tooltip', data-placement='top', :title='getFacultyInstructionalForCode(CODE_INDIVIDUAL_INSTRUCTION).TOOLTIP')
                          font-awesome-icon.mx-1(:icon='["far", "question-circle"]')
                        input.form-control(v-if='canEditWat', type='number', v-model='getFacultyInstructionalForCode(CODE_INDIVIDUAL_INSTRUCTION).ATTRIBUTE_VALUE', :class='getNegativeNumberValidation(getFacultyInstructionalForCode(CODE_INDIVIDUAL_INSTRUCTION).ATTRIBUTE_VALUE)', min='0', @change='roundValue("INSTR", CODE_INDIVIDUAL_INSTRUCTION)')
                        .font-weight-bold(v-else) {{ getFacultyInstructionalForCode(CODE_INDIVIDUAL_INSTRUCTION).ATTRIBUTE_VALUE }}
                        .invalid-feedback Credits must be greater than 0

                    .col-6(v-if='shouldShowValue(getFacultyInstructionalForCode(CODE_OVERLOAD_INSTRUCTION).ATTRIBUTE_VALUE)')
                      CoachmarkTip(:options='getCoachmarkData("9")')
                        label(for='ayOverloadCredits') Overload Instruction - On Duty
                        button.btn.p-0.mb-1.help-icon(v-if='getFacultyInstructionalForCode(CODE_OVERLOAD_INSTRUCTION).TOOLTIP', type='button', data-toggle='tooltip', data-placement='top', :title='getFacultyInstructionalForCode(CODE_OVERLOAD_INSTRUCTION).TOOLTIP')
                          font-awesome-icon.mx-1(:icon='["far", "question-circle"]')
                        input.form-control(v-if='canEditWat', type='number', v-model='getFacultyInstructionalForCode(CODE_OVERLOAD_INSTRUCTION).ATTRIBUTE_VALUE', :class='getNegativeNumberValidation(getFacultyInstructionalForCode(CODE_OVERLOAD_INSTRUCTION).ATTRIBUTE_VALUE)', min='0', @change='roundValue("INSTR", CODE_OVERLOAD_INSTRUCTION)')
                        .font-weight-bold(v-else) {{ getFacultyInstructionalForCode(CODE_OVERLOAD_INSTRUCTION).ATTRIBUTE_VALUE }}
                        .invalid-feedback Credits must be greater than 0
                  .row.form-group(v-if='shouldShowValue(getFacultyInstructionalForCode(CODE_CLASSROOM_ADJ_JUSTIFICATION).ATTRIBUTE_EXPLAIN)')
                    .col-12
                      CoachmarkTip(:options='getCoachmarkData("10")')
                        label(for='teAdjustmentJustification') Classroom {{ equivalencyLabelAbbreviation }} Adjustment Justification
                        button.btn.p-0.mb-1.help-icon(v-if='getFacultyInstructionalForCode(CODE_CLASSROOM_ADJ_JUSTIFICATION).TOOLTIP', type='button', data-toggle='tooltip', data-placement='top', :title='getFacultyInstructionalForCode(CODE_CLASSROOM_ADJ_JUSTIFICATION).TOOLTIP')
                          font-awesome-icon.mx-1(:icon='["far", "question-circle"]')
                        textarea.form-control#teAdjustmentJustification(v-if='canEditWat', v-model='getFacultyInstructionalForCode(CODE_CLASSROOM_ADJ_JUSTIFICATION).ATTRIBUTE_EXPLAIN', :class='getClassroomTEJustificationValidation()')
                        .font-weight-bold(v-else) {{ getFacultyInstructionalForCode(CODE_CLASSROOM_ADJ_JUSTIFICATION).ATTRIBUTE_EXPLAIN }}
                        .invalid-feedback Justification is required if Classroom {{ equivalencyLabelAbbreviation }} is different

                  hr
                  .row.mt-3.mb-0(v-if='classRoomInstructionClasses.length > 0 || individualInstructionClasses.length > 0 || combinedCCClasses.length > 0')
                    .col The table(s) below shows the classes used to estimate the classroom instructional credits
                  .row.mt-0(v-if='is4YearCampus')
                    template(v-if='classRoomInstructionClasses.length > 0 || individualInstructionClasses.length > 0')
                      .col-12
                        CoachmarkTip(:options='getCoachmarkData("11")')
                          .font-weight-bold Fall {{ getAcademicYearStart }} / Spring {{ getAcademicYearEnd }} Classroom Instruction
                          table.data-table.table-bordered.table-striped
                            thead
                              th Term
                              th Course
                              th CRN
                              th XL
                              th Credits
                              th Regs
                              th Work Assignment Credit Hours
                            tbody
                              tr(v-for='classItem in classRoomInstructionClasses')
                                td {{ classItem.TERM }}
                                td {{ classItem.COURSE }}
                                td {{ classItem.UNIQUEID }}
                                td {{ classItem.AC_PRIMARY_SEC_IRO }}
                                td {{ classItem.SH_IRO }}
                                td {{ classItem.REGS_FOR_INV }}
                                td {{ classItem.CREDIT_FOR_WORKLOAD }}
                      .col-12.mt-4
                        .font-weight-bold Fall {{ getAcademicYearStart }} / Spring {{ getAcademicYearEnd }} Individual Instruction
                        table.data-table.table-bordered.table-striped
                          thead
                            th Term
                            th Course
                            th CRN
                            th XL
                            th Credits
                            th Regs
                          tbody
                            tr(v-for='classItem in individualInstructionClasses')
                              td {{ classItem.TERM }}
                              td {{ classItem.COURSE }}
                              td {{ classItem.UNIQUEID }}
                              td {{ classItem.AC_PRIMARY_SEC_IRO }}
                              td {{ classItem.SH_IRO }}
                              td {{ classItem.REGS_FOR_INV }}
                            tr(v-if='individualInstructionClasses.length === 0')
                              td(colspan='6') There are no Indvidual Instruction classes found
                    template(v-else)
                      .col-12 There are no classes found to estimate classroom instruction credits

                  .row.mt-0(v-else)
                    .col-md-6.col-sm-12(v-if='combinedCCClasses.length > 0')
                      .font-weight-bold Fall {{ getAcademicYearStart }} / Spring {{ getAcademicYearEnd }} Classes
                        table.data-table.table-bordered.table-striped
                          thead
                            th Term
                            th Course
                            th CRN
                            th XL
                            th Credits
                            th Regs
                            th Work Assignment Credits
                          tbody
                            tr(v-for='classItem in combinedCCClasses')
                              td {{ classItem.TERM }}
                              td {{ classItem.COURSE }}
                              td {{ classItem.UNIQUEID }}
                              td {{ classItem.AC_PRIMARY_SEC_IRO }}
                              td {{ classItem.SH_IRO }}
                              td {{ classItem.REGS_FOR_INV }}
                              td {{ classItem.CREDIT_FOR_WORKLOAD }}
                    .col-sm-12(v-else) There are no classes found to estimate classroom instruction credits
                .row(v-else)
                  .col There is no Instructional information available

            //- Non-Instructional Teaching Section
            .py-4.mb-3(:class='sessionGetDisplayClass("section")')
              .row
                .col-12
                  .row
                    .col
                      h4.mb-0 Section III - NON-INSTRUCTIONAL
                    .col-auto
                      button.btn.p-0.mb-1.ml-2.collapse-icon.when-opened(type="button" data-toggle="collapse" data-target="#section-noninstructional-collapse")
                        font-awesome-icon.mx-1(:icon='["fas", "chevron-up"]')
                      button.btn.p-0.mb-1.ml-2.collapse-icon.when-closed(type="button" data-toggle="collapse" data-target="#section-noninstructional-collapse")
                        font-awesome-icon.mx-1(:icon='["fas", "chevron-down"]')
                  hr.section-hr
              .collapse.show#section-noninstructional-collapse
                .row
                  .col
                    h5 TOTAL: {{ computedNonInstructionalTE }} {{ equivalencyLabelCaps }} EQUIVALENCIES
                    hr
                CoachmarkTip(:options='getCoachmarkData("12")')
                  .form-row
                    .col-3.font-weight-bold Type
                    .col-2.font-weight-bold {{ equivalencyLabelAbbreviation }} Credits
                    .col-7.font-weight-bold Notes

                .form-row.my-2(v-for='(nonIntsr, rowIndex) in facultyNonInstructional', v-if='nonIntsr.selected')
                  .col-3
                    label.col-form-label {{ nonIntsr.ATTRIBUTE_DESC }}
                    button.btn.p-0.mb-1.help-icon(v-if='nonIntsr.TOOLTIP', type='button', data-toggle='tooltip', data-placement='top', :title='nonIntsr.TOOLTIP')
                      font-awesome-icon.mx-1(:icon='["far", "question-circle"]')
                  .col-2
                    input.form-control(v-if='canEditWat', type='number', v-model='nonIntsr.ATTRIBUTE_VALUE', :class='getNegativeNumberValidation(nonIntsr.ATTRIBUTE_VALUE)', min='0', @change='roundValue("NONINSTR", nonIntsr.ATTRIBUTE_CODE)')
                    .font-weight-bold(v-else) {{ nonIntsr.ATTRIBUTE_VALUE }}
                    .invalid-feedback Credits must be greater than 0
                  .col
                    input.form-control(v-if='canEditWat', type='text', v-model='nonIntsr.ATTRIBUTE_EXPLAIN', :required='nonIntsr.ATTRIBUTE_VALUE != null && nonIntsr.ATTRIBUTE_VALUE.length > 0', :class='getTEValidation(nonIntsr)')
                    .font-weight-bold(v-else) {{ nonIntsr.ATTRIBUTE_EXPLAIN }}
                    .invalid-feedback Notes are required if a value is filled in
                  .col-auto.px-0(v-if='canEditWat')
                    CoachmarkTip(v-if='rowIndex === 0', :options='getCoachmarkData("13")')
                      button.btn.text-danger(@click='removeNonInstrTE(nonIntsr)')
                        font-awesome-icon(:icon='["far", "trash-alt"]')
                    button.btn.text-danger(v-else, @click='removeNonInstrTE(nonIntsr)')
                        font-awesome-icon(:icon='["far", "trash-alt"]')

                template(v-if='canEditWat && nonSelectedNonInstructional.length > 0')
                  CoachmarkTip(:options='getCoachmarkData("14")')
                    .row.mt-4
                      .col Add a {{ equivalencyLabel }} Equivalency
                    .row
                      .col-auto.pr-0
                        select.custom-select(v-model='nonInstrTEToAdd')
                          option(value='') Equivalency Type
                          option(v-for='type in nonSelectedNonInstructional', :value='type.ATTRIBUTE_DESC') {{ type.ATTRIBUTE_DESC }}
                      .col
                        button.btn.btn-success(@click='addNewNonInstrTE', :disabled='!nonInstrTEToAdd')
                          font-awesome-icon.mr-2(:icon='["fas", "plus"]')
                          | Add
                    .row(v-if='nonInstrTypeToAddDesc')
                      .col
                        small.form-text.text-muted {{ nonInstrTypeToAddDesc }}

            //- Comments section
            .py-4(:class='sessionGetDisplayClass("section")')
              .row
                .col-12
                  .row
                    .col
                      h4.mb-0 Section IV - ADDITIONAL INFORMATION & COMMENTS
                    .col-auto
                      button.btn.p-0.mb-1.ml-2.collapse-icon.when-opened(type="button" data-toggle="collapse" data-target="#section-other-collapse")
                        font-awesome-icon.mx-1(:icon='["fas", "chevron-up"]')
                      button.btn.p-0.mb-1.ml-2.collapse-icon.when-closed(type="button" data-toggle="collapse" data-target="#section-other-collapse")
                        font-awesome-icon.mx-1(:icon='["fas", "chevron-down"]')
                  hr.section-hr
              .collapse#section-other-collapse
                template(v-if='hasAnySection4')
                  .row.form-group.mb-0
                    .col-6
                      CoachmarkTip(:options='getCoachmarkData("15")')
                        label(for='grantDollars') Estimated Grant Dollars Brought in
                        button.btn.p-0.mb-1.help-icon(v-if='getFacultySummaryForCode(CODE_GRANT_DOLLARS).TOOLTIP', type='button', data-toggle='tooltip', data-placement='top', :title='getFacultySummaryForCode(CODE_GRANT_DOLLARS).TOOLTIP')
                          font-awesome-icon.mx-1(:icon='["far", "question-circle"]')
                        .input-group
                          .input-group-prepend
                            .input-group-text $
                          input.form-control#grantDollars(type='number', v-model='getFacultySummaryForCode(CODE_GRANT_DOLLARS).ATTRIBUTE_VALUE', :class='getNegativeNumberValidation(getFacultySummaryForCode(CODE_GRANT_DOLLARS).ATTRIBUTE_VALUE)', min='0', @change='roundValue("SUMMARY", CODE_GRANT_DOLLARS)', :readonly='!canEditWat')
                          .invalid-feedback Dollars must be greater than 0
                    .col-6(v-if='shouldShowValue(getFacultySummaryForCode(CODE_OVERLOAD_SUMMER_INSTRUCTION).ATTRIBUTE_VALUE)')
                      CoachmarkTip(:options='getCoachmarkData("16")')
                        label(for='ayOverloadSummerCredits') Overload Instruction - Off Duty
                        button.btn.p-0.mb-1.help-icon(v-if='getFacultySummaryForCode(CODE_OVERLOAD_SUMMER_INSTRUCTION).TOOLTIP', type='button', data-toggle='tooltip', data-placement='top', :title='getFacultySummaryForCode(CODE_OVERLOAD_SUMMER_INSTRUCTION).TOOLTIP')
                          font-awesome-icon.mx-1(:icon='["far", "question-circle"]')
                        input.form-control(v-if='canEditWat', type='number', v-model='getFacultySummaryForCode(CODE_OVERLOAD_SUMMER_INSTRUCTION).ATTRIBUTE_VALUE', :class='getNegativeNumberValidation(getFacultySummaryForCode(CODE_OVERLOAD_SUMMER_INSTRUCTION).ATTRIBUTE_VALUE)', min='0', @change='roundValue("SUMMARY", CODE_OVERLOAD_SUMMER_INSTRUCTION)')
                        .font-weight-bold(v-else) {{ getFacultySummaryForCode(CODE_OVERLOAD_INSTRUCTION).ATTRIBUTE_VALUE }}
                        .invalid-feedback Credits must be greater than 0
                  .row.form-group.pt-2(v-if='shouldShowValue(facultyData.COMMENTS)')
                    .col-12
                      CoachmarkTip(:options='getCoachmarkData("17")')
                        label(for='commentsForm') Comments
                        textarea#commentsForm.form-control(, v-model='facultyData.COMMENTS')
                .row(v-else)
                  .col There are no Additional Information or Comments available

          //- sticky side info box
          .col-4.pr-0
            .sticky-top.has-header#info-box
              .p-3(:class='sessionGetDisplayClass("section")')
                CoachmarkTip(:options='getCoachmarkData("4")')
                  h5 {{ getAcademicYearStart }}-{{ getAcademicYearEnd }} WORK ASSIGNMENT
                  .row
                    .col
                      strong {{ facultyData.EMPLOYEE_NAME }}
                  .row
                    .col
                      strong {{ campusCollege }}
                  .row
                    .col.subtitle {{ departmentTitle }}
                  .row
                    .col.subtitle {{ facultyData.TENURE_TYPE }}
                  .row
                    .col.subtitle(v-if='multipleAppt') This person has multiple appointments: {{ multipleAppt }}

                  hr
                  .row.mt-1.mb-0.shorter-height
                    .col.font-weight-bold Basis:
                    .col-auto.font-weight-bold {{ expectedLoad }}
                  .row.mt-1.mb-2.shorter-height
                    .col
                      small.text-muted Calculation: (FTE * Expected {{ equivalencyLabelAbbreviation }})

                  .row
                    .col Classroom Instruction:
                    .col-auto {{ computedClassroomTE }}
                  .row
                    .col Individual Instruction:
                    .col-auto {{ computedIndividualTE }}
                  .row
                    .col Non-Instructional:
                    .col-auto {{ computedNonInstructionalTE }}
                  .row.mt-1
                    .col.font-weight-bold Total Credit Hours and Equivalencies:
                    .col-auto.font-weight-bold {{ actualLoad }}

                  .row.mt-2
                    .col(v-if='workloadStatusIcon')
                      font-awesome-icon.mr-1(:class='workloadStatusClass', :icon='workloadStatusIcon', :title='hasMetWorkloadStatus')
                      span(:class='workloadStatusClass') {{ hasMetWorkloadStatus }}

                template(v-if='canEditWat')
                  CoachmarkTip(:options='getCoachmarkData("5")')
                    .row.mt-2
                      .col.pr-0
                        label.col-form-label.pb-0(for='#template-status') Template Status
                        select.custom-select#template-status(v-model='currentRecordStatus', :disabled='!canEditWat')
                          option(v-for='status in filteredRecordStatuses', :value='status.value') {{ status.label }}
                      .col-auto.align-self-end
                        button.btn.btn-primary.full-width(type='button', :disabled='!canSave', @click='doSave(false)')
                          font-awesome-icon.mr-1(v-if='isSaving', :icon='["fas", "spinner"]', spin)
                          | Save
                    .row
                      .col
                        small.form-text.text-muted {{ templateHelpText }}
                    .row(v-if='!facultyDataIsValid')
                      .col
                        small.form-text.text-danger
                          | There are errors on the template that need to be corrected before saving.
                    .row.mt-2(v-if='canSeeAudit')
                      .col
                        button.btn.btn-info(type='button', @click='showAuditHistory') Change History
                          font-awesome-icon.ml-2(:icon='["fas", "history"]')

</template>

<script>
import { cloneDeep, concat, each, every, find, filter, first, flatten, has, includes, isEmpty, map, reduce, sortBy } from 'lodash';
import { mapGetters } from 'vuex';
import watResource from '@api/wat/watResource';
import CoachmarkTip from '@components/CoachmarkTip';
import Loading from '@components/Loading';
import watUtil from '@api/wat/watUtil';
import util from '@api/util';
import eventBus from '@api/eventBus';
import { DateTime } from 'luxon';

const _ = { cloneDeep, concat, each, every, find, filter, first, flatten, has, includes, isEmpty, map, reduce, sortBy };
const CAMPUS_4_YEARS = ['MAN', 'HIL', 'WOA'];
const PRECISION_PLACES = 12;

const WORKLOAD_STATUS_MET = 'Workload Basis Completed';
const WORKLOAD_STATUS_NOT_MET = 'Workload Basis Not Completed';
const WORKLOAD_STATUS_NA = 'N/A, Excluded from Report due to\nFaculty Status';
const INVALID_CSS = 'is-invalid';
const STATUS_DRAFT = 'D';
const STATUS_FOR_REVIEW = 'R';
const STATUS_FINAL = 'F';
const LABEL_DRAFT = 'Draft';
const LABEL_FOR_REVIEW = 'For Review';
const LABEL_FINAL = 'Final and Approved';
const CRS_TYPE_INDV = ['1'];
const CRS_TYPE_CLASS = ['2'];
const VALUE_MET = 'Met';
const VALUE_NOT_MET = 'Not Met';

const SMALL_HEADER_TOP_OFFSET = 100;

const KEY_TUTORIAL_KEY = 'watRecord';
const COACHMARKS = [{
  order: '0',
  text: 'Job Type of Employee.  This field is calculated using PeopleSoft/HR data, but can be changed by departments.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '1',
  text: 'Department Full-time Equivalent as it appears in PeopleSoft/HR data.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '2',
  text: 'Expected Teaching Equivalent represents the number of TEs expected based on 1.0 FTE.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '3',
  text: 'Faculty status check boxes are for faculty that were unable to make their workload due to being no longer employed, new/partial year hire, or on another approved leave type other than sabbatical leave.  Selecting any of these checkboxes will move the faculty member to the "N/A" category and to be excluded from the Workload Assignment Report for the year.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '4',
  text: 'The Summary Box is used to provide descriptive information for a faculty member as well as the totals for all teaching types.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '5',
  text: 'Template status is used to view and update the status of an individual record.  The Status options that you can select from the drop-down menu will vary depending on your access level.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '6',
  text: 'Estimated Classroom Instruction calculated by IRAPO from data in Banner.  This number is just an estimate and can be overridden by the “Actual Classroom Instruction” field.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '7',
  text: 'Actual Classroom Instruction: The combined classroom instruction credits given to the faculty member as part of their normal workload.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '8',
  text: 'Individual Instruction: The combined individual instruction credits given to the faculty member as part of their normal workload.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '9',
  text: 'Overload Instruction: The combined credits given to a faculty member (both classroom and individual instruction) as part of overload.  This field is currently optional, but may become required in future years.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '10',
  text: 'If the actual classroom instruction credits (excluding overload) are different from the estimated classroom instruction WE, please enter an explanation as to why.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '11',
  text: 'Classroom Instruction Courses: Automatically populated table.  Table lists the courses used to calculate classroom instruction WE based on course and instructor information that was entered into Banner.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '12',
  text: 'Modify existing Non-Instructional TE categories and add notes with detail here.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '13',
  text: 'If existing Non-Instructional TE categories are no longer used, they can be deleted by clicking on this icon.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '14',
  text: 'If an additional Non-Instructional TE category needs to be added, select the equivalency type followed by the green "+Add" button.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '15',
  text: 'Enter estimated grant dollars brought in.',
  type: KEY_TUTORIAL_KEY
}, {
  order: '16',
  text: 'The combined credits given to a faculty member (both classroom and individual instruction) as part of overload during their off-duty period (i.e., Summer overload)',
  type: KEY_TUTORIAL_KEY
}, {
  order: '17',
  text: 'Enter any additional comments for the Work Assignment Template here.',
  type: KEY_TUTORIAL_KEY
}];

/**
 * Maps true/false to Yes/No.  Incoming value is a string
 */
function booleanTransform(value) {
  switch (value) {
    case 'false':
      return 'No';
    case 'true':
      return 'Yes';
    default:
      return value;
  }
}

const AUDIT_COLUMNS = [{
  value: 'AUDIT_DATE',
  label: 'Timestamp'
}, {
  value: 'AUDIT_USERNAME',
  label: 'Username'
}, {
  value: 'RECORD_STATUS',
  label: 'Record Status',
  transform: (value) => {
    switch (value) {
      case STATUS_DRAFT:
        return LABEL_DRAFT;
      case STATUS_FOR_REVIEW:
        return LABEL_FOR_REVIEW;
      case STATUS_FINAL:
        return LABEL_FINAL;
      default:
        return value;
    }
  }
}, {
  value: 'WORKLOAD_STATUS',
  label: 'Wkload Basis Status',
  transform: (value) => {
    switch (value) {
      case VALUE_MET:
        return 'Completed';
      case VALUE_NOT_MET:
        return 'Not Completed';
      default:
        return value;
    }
  }
}, {
  value: 'JOB_TYPE',
  label: 'Job Type'
}, {
  value: 'FTE',
  label: 'FTE'
}, {
  value: 'FULL_LOAD_CREDITS',
  label: 'Expected Wkload'
}, {
  value: 'YEARL',
  label: 'Entire Year Leave',
  transform: booleanTransform
}, {
  value: 'ANYL',
  label: 'Partial Leave',
  transform: booleanTransform
}, {
  value: 'NEWH',
  label: 'New Hire',
  transform: booleanTransform
}, {
  value: 'NOTEM',
  label: 'No Longer Empl',
  transform: booleanTransform
}, {
  value: 'CLSY',
  label: 'Actual Class Instr'
}, {
  value: 'INDY',
  label: 'Ind Instr'
}, {
  value: 'INDY',
  label: 'Ind Instr'
}, {
  value: 'OVLY',
  label: 'Ovld On Duty'
}, {
  value: 'RES',
  label: 'Research'
}, {
  value: 'SER',
  label: 'Service'
}, {
  value: 'PACTV',
  label: 'Prof Act'
}, {
  value: 'EXT',
  label: 'Extension'
}, {
  value: 'BUY',
  label: 'Buyout'
}, {
  value: 'ADM',
  label: 'Admin'
}, {
  value: 'REL',
  label: 'Release'
}, {
  value: 'SABF',
  label: 'Fall Sabb'
}, {
  value: 'SABS',
  label: 'Spring Sabb'
}, {
  value: 'OTH',
  label: 'Other'
}, {
  value: 'GRANTS',
  label: 'Grant $'
}, {
  value: 'OVLS',
  label: 'Ovld Off Duty'
}];

export default {
  name: 'FacultyAssignment',
  components: {
    CoachmarkTip,
    Loading
  },
  props: {
    facultyId: String,
    fromHome: Boolean,
    rawFacultyData: Object
  },
  data: () => ({
    CODE_GRANT_DOLLARS: 'GRANT',
    CODE_EST_CLASSROOM_INSTRUCTION: 'CLSEY',
    CODE_ACTUAL_CLASSROOM_INSTRUCTON: 'CLS-Y',
    CODE_INDIVIDUAL_INSTRUCTION: 'IND-Y',
    CODE_OVERLOAD_INSTRUCTION: 'OVL-Y',
    CODE_CLASSROOM_ADJ_JUSTIFICATION: 'CLSJU',
    CODE_OVERLOAD_SUMMER_INSTRUCTION: 'OVL-S',

    facultyData: {
      FTE: null,
      FULL_LOAD_CREDITS: null
    },
    isLoading: false,
    showErrorMessage: null,
    saveErrorMessage: null,
    isSaveEnabled: false,
    isSaving: false,
    showToast: false,
    // to hold the record status being selected since we're are doing logic on the record status of the object
    currentRecordStatus: STATUS_DRAFT,
    recordStatuses: [{
      value: STATUS_DRAFT,
      label: LABEL_DRAFT,
      help: 'Setting this record to "Draft" indicates this record is still being worked on.'
    }, {
      value: STATUS_FOR_REVIEW,
      label: LABEL_FOR_REVIEW,
      help: 'Setting this record to "For Review" indicates this record is ready to be reviewed by a Dean/Vice Chancellor or equivalent for final approval.'
    }, {
      value: STATUS_FINAL,
      label: LABEL_FINAL,
      help: 'Setting this record to "Final and Approved" indicates this record has been reviewed and approved for reporting purposes.'
    }],

    scrollOffset: 0,
    nonInstrTEToAdd: '',

    startedTutorial: false,
    formDirty: false
  }),
  computed: {
    ...mapGetters('session', [
      'session',
      'sessionHasRole',
      'sessionWat',
      'sessionGetDisplayClass'
    ]),
    hasEditAccess: function () {
      return _.includes(this.sessionWat.canEdit, this.facultyId) || _.includes(this.sessionWat.canFinalize, this.facultyId);
    },
    canSeeAudit: function () {
      return this.sessionHasRole(util.ROLE_ADMIN_DEBUG);
    },
    canEditWat: function () {
      if (!this.facultyData.RECORD_PHASE) {
        return false;
      }
      else if (this.sessionHasRole(util.ROLE_ADMIN_DEBUG)) {
        // if IRAPO admin, should not be affected by any record phases
        return this.hasEditAccess;
      }
      if (this.facultyData.RECORD_PHASE === watUtil.RECORD_PHASE_FINAL) {
        // if final phase, no can edit
        return false;
      }
      else if (this.recordIsFinal) {
        // if in final status, only if have A role on the record can edit
        return this.roleOnRecord === 'A';
      }
      else if (this.recordIsForReview && !this.canFinalizeWat) {
        // record is read only if can't finalise and it's for review
        return false;
      }
      else if (this.facultyData.RECORD_PHASE === watUtil.RECORD_PHASE_REVIEW && this.roleOnRecord !== watUtil.ROLE_ADMIN && this.roleOnRecord !== watUtil.ROLE_REVIEWER) {
        // if in review phase, must have reviewer/admin roles to edit
        return false
      }
      else {
        return this.hasEditAccess;
      }
    },
    canFinalizeWat: function () {
      return this.sessionWat.canFinalize ? _.includes(this.sessionWat.canFinalize, this.facultyId) : false;
    },
    canSave: function () {
      return this.isSaveEnabled && !this.isSaving && this.facultyDataIsValid && this.canEditWat && _.every(this.facultyData.NONINSTR, nonInstr => this.getTEValidation(nonInstr) !== INVALID_CSS);
    },
    currentRecordStatusLabel: function () {
      const currentStatus = _.find(this.recordStatuses, { value: this.currentRecordStatus });
      return _.has(currentStatus, 'label') ? currentStatus.label.replace('Status: ', '') : '';
    },
    templateHelpText: function () {
      return (_.find(this.recordStatuses, { value: this.currentRecordStatus }) || {}).help;
    },
    filteredRecordStatuses: function () {
      if (this.canFinalizeWat || this.recordIsFinal) {
        return this.recordStatuses;
      }
      else {
        return _.filter(this.recordStatuses, record => record.value !== STATUS_FINAL);
      }
    },
    roleOnRecord: function () {
      return _.has(this.facultyData, 'ROLE') ? this.facultyData.ROLE : null;
    },
    recordIsForReview: function () {
      return _.has(this.facultyData, 'RECORD_STATUS') && this.facultyData.RECORD_STATUS === STATUS_FOR_REVIEW;
    },
    recordIsFinal: function () {
      return _.has(this.facultyData, 'RECORD_STATUS') && this.facultyData.RECORD_STATUS === STATUS_FINAL;
    },
    targetIsForReview: function () {
      return this.currentRecordStatus === STATUS_FOR_REVIEW;
    },
    targetIsFinal: function () {
      return this.currentRecordStatus === STATUS_FINAL;
    },
    shouldCheckValidation: function () {
      return this.currentRecordStatus !== STATUS_DRAFT;
    },
    campusCollege: function () {
      // campus and college
      let toReturn = this.facultyData.CAMPUS || '';
      if (!_.isEmpty(toReturn)) {
        toReturn += ',';
      }

      if (!_.isEmpty(this.facultyData.EAC_DIVISION_DESC)) {
        toReturn += ` ${this.facultyData.EAC_DIVISION_DESC}`;
      }
      return toReturn;
    },
    departmentTitle: function () {
      // department and job title
      let toReturn = this.facultyData.EAC_SECTION_DESC || '';
      if (!_.isEmpty(toReturn)) {
        toReturn += ',';
      }

      if (!_.isEmpty(this.facultyData.JOB_TITLE)) {
        toReturn += ` ${this.facultyData.JOB_TITLE}`;
      }
      return toReturn;
    },
    multipleAppt: function () {
      return _.has(this.facultyData, 'MULTIPLE_APPT') ? this.facultyData.MULTIPLE_APPT : null;
    },
    facultyStatuses: function () {
      return _.sortBy(this.facultyData.STATUS, status => parseInt(status.DISPLAY_ORDER));
    },
    facultySummary: function () {
      return _.sortBy(this.facultyData.SUMMARY, status => parseInt(status.DISPLAY_ORDER));
    },
    facultyNonInstructional: function () {
      return _.sortBy(this.facultyData.NONINSTR, status => parseInt(status.DISPLAY_ORDER));
    },
    nonSelectedNonInstructional: function () {
      return _.filter(this.facultyData.NONINSTR, { selected: false });
    },
    nonInstrTypeToAddDesc: function () {
      const toAdd = _.find(this.facultyData.NONINSTR, { ATTRIBUTE_DESC: this.nonInstrTEToAdd });
      return toAdd ? toAdd.TOOLTIP : null;
    },
    individualInstructionClasses: function () {
      if (_.has(this.facultyData, 'CLASSES')) {
        const classes = _.filter(this.facultyData.CLASSES, classObj => _.includes(CRS_TYPE_INDV, classObj.CRS_TYPE));
        return _.sortBy(_.map(classes, this.parseClassroomObject), ['SEM_YR_IRO', 'UNIQUEID']);
      }
      return [];
    },
    classRoomInstructionClasses: function () {
      if (_.has(this.facultyData, 'CLASSES')) {
        const classes = _.filter(this.facultyData.CLASSES, classObj => _.includes(CRS_TYPE_CLASS, classObj.CRS_TYPE));
        return _.sortBy(_.map(classes, this.parseClassroomObject), ['SEM_YR_IRO', 'UNIQUEID']);
      }
      return [];
    },
    combinedCCClasses: function () {
      return _.sortBy(_.map(this.facultyData.CLASSES, this.parseClassroomObject), ['SEM_YR_IRO', 'UNIQUEID']);
    },
    is4YearCampus: function () {
      return _.includes(CAMPUS_4_YEARS, this.facultyData.CAMPUS);
    },
    facultyDataIsValid: function () {
      let isValid = true;

      // short circuit out if no need to check
      if (!this.shouldCheckValidation) {
        return isValid;
      }

      // check non instructional teaching equivalents; if credits are there, notes are required
      isValid = _.every(this.facultyData.NONINSTR, (row) => {
        if (_.isEmpty(row.ATTRIBUTE_VALUE)) {
          return true;
        }
        else {
          return !_.isEmpty(row.ATTRIBUTE_VALUE) && !_.isEmpty(row.ATTRIBUTE_EXPLAIN);
        }
      });

      // check numbers entered are positive
      if (isValid) {
        isValid = _.every(_.concat(this.facultyData.INSTR, this.facultyData.SUMMARY), (row) => {
          // if not a number type or it's empty, it's valid
          if (row.ATTRIBUTE_FORMAT !== 'N' || _.isEmpty(row.ATTRIBUTE_VALUE)) {
            return true;
          }
          return parseFloat(row.ATTRIBUTE_VALUE) >= 0;
        });
      }

      // check the justification
      if (isValid) {
        isValid = this.getClassroomTEJustificationValidation().indexOf(INVALID_CSS) < 0;
      }

      return isValid;
    },
    computedNonInstructionalTE: function () {
      const total = _.reduce(this.facultyData.NONINSTR, (sum, row) => {
        return sum + parseFloat(row.ATTRIBUTE_VALUE || 0);
      }, 0);
      return parseFloat(total.toPrecision(PRECISION_PLACES));
    },
    computedInstructionalTE: function () {
      return parseFloat((this.computedIndividualTE + this.computedClassroomTE).toPrecision(PRECISION_PLACES));
    },
    computedIndividualTE: function () {
      const total = parseFloat(this.getFacultyInstructionalForCode(this.CODE_INDIVIDUAL_INSTRUCTION).ATTRIBUTE_VALUE || 0);
      return parseFloat((total).toPrecision(PRECISION_PLACES));
    },
    computedClassroomTE: function () {
      const total = parseFloat(this.getFacultyInstructionalForCode(this.CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_VALUE || 0);
      return parseFloat((total).toPrecision(PRECISION_PLACES));
    },
    expectedLoad: function () {
      return parseFloat((parseFloat(this.facultyData.FTE || 0) * parseFloat(this.facultyData.FULL_LOAD_CREDITS || 0)).toPrecision(PRECISION_PLACES));
    },
    actualLoad: function () {
      return parseFloat((this.computedInstructionalTE + this.computedNonInstructionalTE).toPrecision(PRECISION_PLACES));
    },
    hasAnyFacultyStatus: function () {
      return _.find(this.facultyStatuses, { ATTRIBUTE_VALUE: true });
    },
    hasMetWorkloadStatus: function () {
      // if any status is checked, it's N/A
      if (this.hasAnyFacultyStatus) {
        return WORKLOAD_STATUS_NA;
      }

      // they are eligible to be met or not met so calculate
      return this.actualLoad >= this.expectedLoad ? WORKLOAD_STATUS_MET : WORKLOAD_STATUS_NOT_MET;
    },
    workloadStatusClass: function () {
      if (this.hasMetWorkloadStatus === WORKLOAD_STATUS_MET) {
        return 'text-success';
      }
      else if (this.hasMetWorkloadStatus === WORKLOAD_STATUS_NOT_MET) {
        return 'text-warning';
      }
      else {
        return 'text-info';
      }
    },
    workloadStatusIcon: function () {
      if (this.hasMetWorkloadStatus === WORKLOAD_STATUS_MET) {
        return ['far', 'check-circle'];
      }
      else if (this.hasMetWorkloadStatus === WORKLOAD_STATUS_NOT_MET) {
        return ['fas', 'exclamation-triangle'];
      }
      else {
        return ['fas', 'info-circle'];
      }
    },
    recordStatusClass: function () {
      if (this.recordIsFinal) {
        return 'bg-success';
      }
      else if (this.recordIsForReview) {
        return 'bg-info';
      }
      else {
        return 'bg-secondary';
      }
    },
    getAcademicYearStart: function () {
      // converts the reporting year of the record into the academic year string
      if (_.has(this.facultyData, 'REPORTING_YEAR')) {
        const previousYear = parseFloat(this.facultyData.REPORTING_YEAR) - 1;
        return `${previousYear}`;
      }
      else {
        return '';
      }
    },
    getAcademicYearEnd: function () {
      return this.facultyData.REPORTING_YEAR;
    },
    toastPosition: function () {
      // if we're not showing the toast,  make it off screen so it doesnt block the save button
      return `position: fixed; top: ${this.showToast ? '40px' : '-100px'}; right: 1rem; z-index: 1050;`;
    },
    jobTypes: function () {
      return watUtil.WAT_JOB_TYPES;
    },
    equivalencyLabel: function () {
      return this.facultyData.JOB_TYPE === watUtil.JOB_TYPE_INSTRUCTIONAL ? 'Teaching' : 'Workload';
    },
    equivalencyLabelTooltip: function () {
      return `This value is relative to 1.0 FTE.  This is used to calculate the faculty's Basis (expected ${this.equivalencyLabel} equivalency) based on the FTE specified.`;
    },
    equivalencyLabelCaps: function () {
      return this.equivalencyLabel.toUpperCase();
    },
    equivalencyLabelAbbreviation: function () {
      return this.facultyData.JOB_TYPE === watUtil.JOB_TYPE_INSTRUCTIONAL ? 'TE' : 'WE';
    },
    getStatusLabel: function () {
      let message = '';
      let phaseMessage = '';

      if (!this.facultyData.RECORD_PHASE) {
        const dateObject = DateTime.fromISO(watUtil.RECORD_RELEASE_DATE);
        return `This record is in a pre-release preview.  It will be available for editing on ${dateObject.toLocaleString(DateTime.DATE_HUGE)} at ${dateObject.toLocaleString(DateTime.TIME_SIMPLE)}`;
      }

      if (this.facultyData.RECORD_PHASE === watUtil.RECORD_PHASE_FINAL) {
        phaseMessage = '.  [Data collection closed for ALL users]';
      }
      else if (this.facultyData.RECORD_PHASE === watUtil.RECORD_PHASE_REVIEW) {
        phaseMessage = '.  [Data collection closed for general users]';
      }

      if (this.facultyData.RECORD_STATUS === STATUS_DRAFT) {
        message = "This record is still in progress (DRAFT)";
      }
      else if (this.recordIsForReview) {
        message = "This record is ready for review (FOR REVIEW)";
        if (!this.canFinalizeWat && this.hasEditAccess) {
          message = `${message}.  If you need to make changes, please contact your Dean/Vice Chancellor.`;
          phaseMessage = '';
        }
      }
      else if (this.facultyData.RECORD_STATUS === STATUS_FINAL) {
        message = "This record has been finalized and approved (FINAL AND APPROVED)";
      }
      return `${message}${phaseMessage}`;
    },
    getBadgeClass: function () {
      if (this.facultyData.RECORD_STATUS === STATUS_DRAFT) {
        return 'badge-secondary';
      }
      else if (this.recordIsForReview || !this.facultyData.RECORD_PHASE) {
        return 'badge-info';
      }
      else {
        return 'badge-success';
      }
    },
    badgeLabel: function () {
      if (this.facultyData.RECORD_STATUS === STATUS_DRAFT) {
        return LABEL_DRAFT;
      }
      else if (this.recordIsForReview) {
        return LABEL_FOR_REVIEW;
      }
      else {
        return LABEL_FINAL;
      }
    },
    hasAnyInstructionalData: function () {
      return this.shouldShowValue(this.getFacultyInstructionalForCode(this.CODE_EST_CLASSROOM_INSTRUCTION).ATTRIBUTE_VALUE) ||
              this.shouldShowValue(this.getFacultyInstructionalForCode(this.CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_VALUE) ||
              this.shouldShowValue(this.getFacultyInstructionalForCode(this.CODE_INDIVIDUAL_INSTRUCTION).ATTRIBUTE_VALUE) ||
              this.shouldShowValue(this.getFacultyInstructionalForCode(this.CODE_OVERLOAD_INSTRUCTION).ATTRIBUTE_VALUE) ||
              this.shouldShowValue(this.getFacultyInstructionalForCode(this.CODE_CLASSROOM_ADJ_JUSTIFICATION).ATTRIBUTE_EXPLAIN);
    },
    hasAnySection4: function () {
      return this.shouldShowValue(this.getFacultySummaryForCode(this.CODE_GRANT_DOLLARS).ATTRIBUTE_VALUE) ||
              this.shouldShowValue(this.facultyData.COMMENTS) ||
              this.shouldShowValue(this.getFacultySummaryForCode(this.CODE_OVERLOAD_SUMMER_INSTRUCTION).ATTRIBUTE_VALUE);
    },
    auditColumns: function () {
      const AUDIT_FIELDS_REQUIRED = ['AUDIT_DATE', 'AUDIT_USERNAME'];
      const auditEntries = this.facultyData.fullAudit || [];
      const changedFields = _.flatten(_.map(auditEntries, entry => entry.changed || []));

      // if it's a required field we're showing or there's a change to the field
      return _.filter(AUDIT_COLUMNS, field => _.includes(AUDIT_FIELDS_REQUIRED, field.value) || _.includes(changedFields, field.value));
    }
  },
  methods: {
    makeDirty: function () {
      this.$emit('make-dirty');
    },
    updateNonInstructional: function (nonInstructionals) {
      return _.map(nonInstructionals, (data) => {
        data.selected = _.has(data, 'ATTRIBUTE_VALUE') || _.has(data, 'ATTRIBUTE_EXPLAIN');
        return data;
      });
    },
    async init() {
      this.isLoading = true;
      try {
        const facultyDataResponse = _.cloneDeep(this.rawFacultyData);

        // convert the status string bools to booleans
        facultyDataResponse.STATUS = _.map(facultyDataResponse.STATUS, (status) => {
          if (_.has(status, 'ATTRIBUTE_VALUE')) {
            status.ATTRIBUTE_VALUE = status.ATTRIBUTE_VALUE === 'true';
          }
          return status;
        });

        // add on the selected attribute for each type
        facultyDataResponse.NONINSTR = this.updateNonInstructional(facultyDataResponse.NONINSTR);

        // run any audit entry translations
        const hasTransforms = _.filter(AUDIT_COLUMNS, column => column.transform);
        _.each(hasTransforms, (auditCol) => {
          _.each(facultyDataResponse.fullAudit, (auditEntry) => {
            if (auditEntry[auditCol.value]) {
              auditEntry[auditCol.value] = auditCol.transform(auditEntry[auditCol.value]);
            }
          });
        });

        this.facultyData = facultyDataResponse;

        // update session template roles in the session
        this.$store.dispatch('session/setSessionWatTemplateRoles', [{
          id: this.facultyData.FACULTY_RECORD_UID,
          role: this.facultyData.ROLE,
        }]);

        // make sure to copy in so we can update it
        this.currentRecordStatus = this.facultyData.RECORD_STATUS;

        // determine if instructional related sections should be shown
        $('#section-instructional-collapse').addClass(this.getInstructionalShowClass());
        $('#section-other-collapse').addClass(this.getSection4ShowClass());
      }
      catch (e) {
        console.error(e);
        if (_.has(e, 'body.message')) {
          this.showErrorMessage = e.body.message;
        }
        else {
          this.showErrorMessage = 'Whoops, there was an error loading the content.';
        }
      }
      finally {
        this.isLoading = false;
      }
    },
    doSaveAsDraft() {
      return this.doSave(STATUS_DRAFT);
    },
    async doSave(recordStatusOverride) {

      // show the save confirmation if we're not overriding and not draft
      if (this.currentRecordStatus !== STATUS_DRAFT && !recordStatusOverride) {
        $('#modalSaveConfirm').modal();
        return;
      }

      // set the record status to the override if it's there
      if (recordStatusOverride) {
        this.currentRecordStatus = recordStatusOverride;
      }

      this.isSaving = true;
      this.showToast = true;
      this.saveErrorMessage = null;
      try {
        // set the computed noninstructional TE (must be a string)
        const nonInstructional = _.find(this.facultyData.SUMMARY, { ATTRIBUTE_CODE: 'NONIN' });
        if (nonInstructional) {
          nonInstructional.ATTRIBUTE_VALUE = `${this.computedNonInstructionalTE}`;
        }

        // set the computed instructional TE adjustments
        const fallInstructionalAdjustment = this.getFacultyInstructionalForCode('CLSFA');
        fallInstructionalAdjustment.ATTRIBUTE_VALUE = `${this.getClassroomTEDifference("F")}`;
        const springInstructionalAdjustment = this.getFacultyInstructionalForCode('CLSSA');
        springInstructionalAdjustment.ATTRIBUTE_VALUE = `${this.getClassroomTEDifference("S")}`;

        // also copy the updated record status, we'll clone it so we don't mutate our existing
        // in the event of an error
        const dataToSave = _.cloneDeep(this.facultyData);
        dataToSave.RECORD_STATUS = this.currentRecordStatus;

        // do the save
        this.facultyData = await watResource.put(`api/w/facstaff/wat/${this.facultyId}`, dataToSave, null, this.$route.query);

        // convert the status string bools to booleans
        this.facultyData.STATUS = _.map(this.facultyData.STATUS, (status) => {
          status.ATTRIBUTE_VALUE = status.ATTRIBUTE_VALUE === 'true';
          return status;
        });

        // add on the selected attribute for each type
        this.facultyData.NONINSTR = this.updateNonInstructional(this.facultyData.NONINSTR);

        // run any audit entry translations
        const hasTransforms = _.filter(AUDIT_COLUMNS, column => column.transform);
        _.each(hasTransforms, (auditCol) => {
          _.each(this.facultyData.audit, (auditEntry) => {
            if (auditEntry[auditCol.value]) {
              auditEntry[auditCol.value] = auditCol.transform(auditEntry[auditCol.value]);
            }
          });
        });


        this.isSaveEnabled = false;
        $('.success-toast').toast('show');
        this.formDirty = false;

        // fire the event to notify the list page
        eventBus.bus.$emit(eventBus.CHANNEL_WAT_RECORD_UPDATED, {
          FACULTY_RECORD_UID: this.facultyData.FACULTY_RECORD_UID,
          RECORD_STATUS: this.facultyData.RECORD_STATUS,
          WORKLOAD_STATUS: this.hasMetWorkloadStatus === WORKLOAD_STATUS_MET ? 'Met' : this.hasMetWorkloadStatus === WORKLOAD_STATUS_NOT_MET ? 'Not Met' : 'N/A',
          BASIS: this.facultyData.FULL_LOAD_CREDITS,
          WAU: this.actualLoad
        });
      }
      catch (saveErr) {
        console.error(saveErr);
        if (_.has(saveErr, 'body.message')) {
          this.saveErrorMessage = saveErr.body.message;
        }
        $('.error-toast').toast('show');
      }
      finally {
        this.isSaving = false;
      }
    },
    shouldShowValue: function (value) {
      return this.canEditWat || !_.isEmpty(value);
    },
    onJobTypeChange(facultyObj) {
      facultyObj.FULL_LOAD_CREDITS = watUtil.getExpectedWorkload(facultyObj.CAMPUS, facultyObj.JOB_TYPE, facultyObj.JOB_CODE);
    },
    getClassroomTEDifference(semester) {
      const code = semester === 'F' ? 'CLS-F' : 'CLS-S';
      const estimatedCode = semester === 'F' ? 'CLSFE' : 'CLSSE';
      const value = this.getFacultyInstructionalForCode(code);
      const estimatedValue = this.getFacultyInstructionalForCode(estimatedCode);
      const diff = parseFloat(value.ATTRIBUTE_VALUE || 0) - parseFloat(estimatedValue.ATTRIBUTE_VALUE || 0);
      return isNaN(diff) ? 0 : diff;
    },
    getTEValidation(nonIntsrRow) {
      let toReturn = '';
      if (this.shouldCheckValidation) {
        const explainValue = nonIntsrRow.ATTRIBUTE_EXPLAIN || '';

        // if there's a value and no explain
        if (!_.isEmpty(nonIntsrRow.ATTRIBUTE_VALUE) && _.isEmpty(explainValue.trim())) {
          toReturn = INVALID_CSS;
        }
      }
      return toReturn;
    },
    getNegativeAndRequiredValidation(value) {
      let toReturn = this.getNegativeNumberValidation(value);
      if (_.isEmpty(toReturn)) {
        toReturn = this.getRequiredValidation(value);
      }
      return toReturn;
    },
    getNegativeNumberValidation(value) {
      let toReturn = '';
      if (this.shouldCheckValidation) {
        const val = parseFloat(value);

        // if there is a value and it's not a number or less than 0, it's invalid
        if (!_.isEmpty(value) && (isNaN(val) || val < 0)) {
          toReturn = INVALID_CSS;
        }
      }
      return toReturn;
    },
    getRequiredValidation(value) {
      return _.isEmpty(value) ? INVALID_CSS : '';
    },
    getFacultySummaryForCode(code) {
      return _.find(this.facultyData.SUMMARY, { ATTRIBUTE_CODE: code }) || {};
    },
    getFacultyInstructionalForCode(code) {
      return _.find(this.facultyData.INSTR, { ATTRIBUTE_CODE: code }) || {};
    },
    getClassroomTEJustificationValidation() {
      let toReturn = '';

      if (this.shouldCheckValidation) {
        const actualClassroom = this.getFacultyInstructionalForCode(this.CODE_ACTUAL_CLASSROOM_INSTRUCTON);
        const estimatedClassroom = this.getFacultyInstructionalForCode(this.CODE_EST_CLASSROOM_INSTRUCTION);

        // default to 0 if undefined
        const isDifferent = parseFloat(actualClassroom.ATTRIBUTE_VALUE || 0) !== parseFloat(estimatedClassroom.ATTRIBUTE_VALUE || 0);
        if (isDifferent && _.isEmpty(this.getFacultyInstructionalForCode(this.CODE_CLASSROOM_ADJ_JUSTIFICATION).ATTRIBUTE_EXPLAIN)) {
          toReturn = INVALID_CSS;
        }
      }

      return toReturn;
    },
    roundValue(type, code, places) {
      const placesToUse = places || 2;
      const obj = _.find(this.facultyData[type], { ATTRIBUTE_CODE: code }) || {};
      const numberValue = parseFloat(obj.ATTRIBUTE_VALUE);
      if (!isNaN(numberValue)) {
        obj.ATTRIBUTE_VALUE = parseFloat(numberValue.toFixed(placesToUse)).toString();
      }
    },
    roundTopLevelValue(type, places) {
      const placesToUse = places || 2;
      const numberValue = parseFloat(this.facultyData[type]);
      if (!isNaN(numberValue)) {
        this.facultyData[type] = parseFloat(numberValue.toFixed(placesToUse)).toString();
      }
    },
    getRouterLink(name) {
      return {
        name,
        query: this.$route.query
      }
    },
    parseClassroomObject(classRecord) {
      const toReturn = classRecord;
      toReturn.TERM = classRecord.SEM_YR_IRO.indexOf('-8') >= 0 ? 'Fall' : 'Spring';
      return toReturn;
    },
    scrollToTop: function () {
      $('html,body').scrollTop(0);
    },
    onScroll: function () {
      const SHADOW_CLASS = 'shadow';
      const stickyHeader = document.getElementById('the-sticky-header');
      if (stickyHeader) {
        if (window.pageYOffset > SMALL_HEADER_TOP_OFFSET) {
          // stickyHeader.classList.add('show-small');
          stickyHeader.classList.add(SHADOW_CLASS);
        }
        else {
          // stickyHeader.classList.remove('show-small');
          stickyHeader.classList.remove(SHADOW_CLASS);
        }

        const isNavHidden = document.getElementsByClassName('hideable-header-hidden').length > 0;
        if (isNavHidden) {
          stickyHeader.classList.add('nav-hidden');
        }
        else {
          stickyHeader.classList.remove('nav-hidden');
        }
      }
    },
    updateHeader: function (options) {
      this.isHeaderShown = options.isShown;

      const infoBox = document.getElementById('info-box');
      if (infoBox) {
        if (options.isShown) {
          infoBox.classList.add('has-header');
        }
        else {
          infoBox.classList.remove('has-header');
        }
      }
    },
    addNewNonInstrTE: function () {
      const toAdd = _.find(this.facultyData.NONINSTR, { ATTRIBUTE_DESC: this.nonInstrTEToAdd });
      if (toAdd) {
        this.makeDirty();
        toAdd.selected = true;
      }
      this.nonInstrTEToAdd = '';
    },
    removeNonInstrTE: function (nonInstr) {
      this.makeDirty();
      nonInstr.ATTRIBUTE_VALUE = null;
      nonInstr.ATTRIBUTE_EXPLAIN = null;
      nonInstr.selected = false;
    },
    getReadOnlyText: function (value) {
      return _.isEmpty(value) ? '-' : value;
    },
    getCoachmarkData: function (order) {
      return _.find(COACHMARKS, { order });
    },
    getInstructionalShowClass: function () {
      if (this.facultyData.JOB_TYPE === watUtil.JOB_TYPE_INSTRUCTIONAL) {
        return 'show';
      }
      else {
        const allEmpty = _.isEmpty(this.getFacultyInstructionalForCode(this.CODE_EST_CLASSROOM_INSTRUCTION).ATTRIBUTE_VALUE) &&
             _.isEmpty(this.getFacultyInstructionalForCode(this.CODE_ACTUAL_CLASSROOM_INSTRUCTON).ATTRIBUTE_VALUE) &&
             _.isEmpty(this.getFacultyInstructionalForCode(this.CODE_INDIVIDUAL_INSTRUCTION).ATTRIBUTE_VALUE) &&
             _.isEmpty(this.getFacultyInstructionalForCode(this.CODE_OVERLOAD_INSTRUCTION).ATTRIBUTE_VALUE) &&
             _.isEmpty(this.getFacultyInstructionalForCode(this.CODE_CLASSROOM_ADJ_JUSTIFICATION).ATTRIBUTE_EXPLAIN);
        return !allEmpty ? 'show' : '';
      }
    },
    getSection4ShowClass: function () {
      if (this.facultyData.JOB_TYPE === watUtil.JOB_TYPE_INSTRUCTIONAL) {
        return 'show';
      }
      else {
        const allEmpty = _.isEmpty((this.getFacultySummaryForCode(this.CODE_GRANT_DOLLARS).ATTRIBUTE_VALUE)) && _.isEmpty((this.facultyData.COMMENTS));
        return !allEmpty ? 'show' : '';
      }
    },
    showAuditHistory: function () {
      $('#modalAudit').modal();
    },
    includes: function (values, value) {
      return _.includes(values, value);
    }
  },
  mounted: function () {
    $('.success-toast').on('hidden.bs.toast', () => {
      this.showToast = false;
    });
    $('.error-toast').on('hidden.bs.toast', () => {
      this.showToast = false;
    });

    window.addEventListener('scroll', this.onScroll);

    // ready to show the support button
    eventBus.bus.$emit(eventBus.CHANNEL_GLOBAL_HELP_BUTTON, {
      type: KEY_TUTORIAL_KEY,
      visible: true
    });

    // window.addEventListener('beforeunload', this.onPageLeave);
  },
  beforeRouteLeave: function (_to, _from, next) {
    // If the form is dirty and the user did not confirm leave,
    // prevent losing unsaved changes by canceling navigation
    if (this.formDirty) {
      next(window.confirm('Are you sure you want to leave the page?  You have unsaved changes.  Please save your changes if you want to keep them.  Click Cancel to stay on the page.'));
    }
    else {
      next();
    }
  },
  created: function () {
    this.init();

    // add a listener to movement of the header
    eventBus.bus.$on(eventBus.CHANNEL_HEADER_MOVEMENT, this.updateHeader);
  },
  watch: {
    facultyData: {
      deep: true,
      handler: function (_updated, old) {
        // don't handle the first time loading the faculty data
        if (!old.FTE) {
          this.isSaveEnabled = true;
        }
        else {
          this.$emit('make-dirty');
        }
      }
    }
  },
  updated: function () {
    $(() => {
      $('[data-toggle="tooltip"]').tooltip()
    });

    if (!this.startedTutorial) {
      eventBus.bus.$emit(eventBus.CHANNEL_CLICKED_HELP, {
        type: KEY_TUTORIAL_KEY
      });
      this.startedTutorial = true;
    }
  },
  beforeDestroy: function () {
    // window.removeEventListener('beforeunload', this.onPageLeave);
    window.removeEventListener('scroll', this.onScroll)
    eventBus.bus.$off(eventBus.CHANNEL_HEADER_MOVEMENT, this.updateHeader);
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="stylus" scoped>
  @import '../../assets/app.styl'

  .form-top-padding
    padding-top 20px

  .shorter-height
    line-height 14px

  .bg
    background-color #eeeeee

  .bg-dark
    background-color dark-mode-background-color

  .noninstr-type
    border 1px solid black
    border-radius .25rem

  .full-width
    width 100%

  .subtitle
    font-size .9rem

  .extra-padding
    padding-top 50px

  .sticky-top
    top 70px !important
    transition 0.2s all ease-out
    z-index auto

  .sticky-top.has-header
    top 150px !important
    transition 0.2s all ease-out

  .form-check
    input, label
      cursor pointer

  .sticky-header
    padding-top 90px
    padding-left 1rem
    padding-right 1rem
    background white
    z-index 99
    position fixed
    top 0px
    left 0px
    width 100%

  .sticky-header-dark
    background dark-background-color !important

  .left-col-border
    border-left 1px solid black
  .right-col-border
    border-right 1px solid black

  .left-col-border-dark
    border-left 1px solid dark-mode-text-color
  .right-col-border-dark
    border-right 1px solid dark-mode-text-color

  .section-hr
    margin-top 0
    border-top 2px solid black

  .section
    background white
    border-radius 1rem
    padding-left 15px
    padding-right 15px

  .section-dark
    background-color dark-mode-background-color

  .help-icon
    font-size .9rem

  .sem-width
    width 150px

  .fine-print
    font-size .75rem

  .show-small
    transform translate3d(0, 0, 0)
    transition 0.2s all ease-out

    .record-status
      position absolute
      height 0 !important
      transform translate3d(0, -300%, 0)
      transition 0.2s all ease-out
    .small-header-name
      display block !important
      transition 0.2s all ease-out

  .nav-hidden
    padding-top 10px !important
    transition 0.2s all ease-out

  .collapsed.when-opened
    display none
  :not(.collapsed).when-closed
    display none

  .faculty-status
    border 1px solid #ced4da
    border-radius .25rem

  .smaller-text
    font-size .75rem

  .modal-wide
    max-width 90%

  @media (prefers-color-scheme: dark)
    .sticky-header-system
      background dark-background-color !important
</style>